import { formatCurrency } from "../../helpers/estimateTotals";
import { formatTaxLineLabel } from "../../helpers/estimateTax";
import { getEstimateTotalPresentation } from "../../helpers/estimateReport";
import { PDF_COLORS, pdfGoldRule } from "./pdfTheme";

export const TABLE_LAYOUT = {
  hLineWidth: () => 0.5,
  vLineWidth: () => 0.5,
  hLineColor: () => PDF_COLORS.border,
  vLineColor: () => PDF_COLORS.border,
  paddingLeft: () => 8,
  paddingRight: () => 8,
  paddingTop: () => 5,
  paddingBottom: () => 5,
};


export function metaFieldInlineCell(label, value) {
  return {
    text: [
      {
        text: `${String(label).toUpperCase()}: `,
        style: "metaLabel",
        margin: [0, 0, 0, 0],
      },
      { text: value || "—", style: "metaValue" },
    ],
    alignment: "left",
    margin: [0, 0, 0, 4],
  };
}

export function buildBrandHeaderBlock({
  logoDataUrl,
  docTitle,
  panelTitle,
  fields = [],
  branding,
}) {
  const brandName = branding?.brandName || "Organization";
  const brandStackItems = [
    { text: brandName, style: "brandName" },
  ];

  if (branding?.legalNameLine) {
    brandStackItems.push({
      text: branding.legalNameLine,
      style: "companyTagline",
    });
  }

  brandStackItems.push({ text: docTitle, style: "docTitle" });

  if (branding?.contactLines?.length) {
    branding.contactLines.forEach((line) => {
      brandStackItems.push({ text: line, style: "companyTagline" });
    });
  }

  const brandStack = {
    stack: brandStackItems,
    margin: logoDataUrl ? [10, 0, 0, 0] : [0, 0, 0, 0],
  };

  const brandRow = logoDataUrl
    ? {
        columns: [{ image: logoDataUrl, width: 44, height: 44 }, brandStack],
        columnGap: 8,
      }
    : brandStack;

  const content = [brandRow, pdfGoldRule()];

  if (fields.length > 0) {
    content.push(
      { text: panelTitle, style: "panelTitle" },
      {
        stack: fields.map((field) =>
          metaFieldInlineCell(field.label, field.value),
        ),
        margin: [0, 0, 0, 4],
        alignment: "left",
      },
    );
  }

  return content;
}

export function buildReportBrandHeader({
  logoDataUrl,
  docTitle,
  panelTitle,
  fields = [],
  branding,
}) {
  return buildBrandHeaderBlock({
    logoDataUrl,
    docTitle,
    panelTitle,
    fields,
    branding,
  });
}

export function buildItemRow(row) {
  const nameStack = [{ text: row.name, style: "itemName" }];

  if (row.description) {
    nameStack.push({ text: row.description, style: "itemDetail" });
  }

  if (row.date) {
    nameStack.push({ text: row.date, style: "itemDetail" });
  }

  if (row.detail) {
    nameStack.push({ text: row.detail, style: "itemDetail" });
  }

  if (row.notes) {
    nameStack.push({ text: `Notes: ${row.notes}`, style: "itemDetail" });
  }

  const columns = [{ width: "*", stack: nameStack }];

  if (row.amount != null) {
    columns.push({
      width: 72,
      text: formatCurrency(row.amount),
      style: "itemAmount",
    });
  }

  return { columns, columnGap: 10, margin: [0, 1, 0, 1] };
}

export function totalsRow(label, amount, style = "totalsRow") {
  return {
    columns: [
      { width: "*", text: label, style },
      { width: 90, text: formatCurrency(amount), style, alignment: "right" },
    ],
  };
}

export function textTotalsRow(label, value, style = "totalsRow") {
  return {
    columns: [
      { width: "*", text: label, style },
      { width: 90, text: value, style, alignment: "right" },
    ],
  };
}

export function buildTotalsPanel(rows) {
  return {
    columns: [
      { width: "*", text: "" },
      {
        width: 220,
        stack: [
          {
            border: [true, true, true, true],
            borderColor: PDF_COLORS.border,
            fillColor: PDF_COLORS.panelBg,
            margin: [10, 8, 10, 8],
            stack: rows,
          },
        ],
      },
    ],
  };
}

export function buildGrandTotalSectionFromPresentation(presentation) {
  if (!presentation) return null;

  const rows = [];

  if (presentation.mode === "withTax") {
    rows.push(totalsRow("Subtotal", presentation.subtotal));
    presentation.lines.forEach((line) => {
      rows.push(totalsRow(formatTaxLineLabel(line), line.amount));
    });
    rows.push({
      columns: [
        { width: "*", text: presentation.totalLabel, style: "totalsGrand" },
        {
          width: 90,
          text: formatCurrency(presentation.total),
          style: "totalsGrand",
          alignment: "right",
        },
      ],
    });
  } else {
    rows.push({
      columns: [
        { width: "*", text: presentation.totalLabel, style: "totalsGrand" },
        {
          width: 90,
          text: formatCurrency(presentation.total),
          style: "totalsGrand",
          alignment: "right",
        },
      ],
    });
  }

  return [pdfGoldRule(), buildTotalsPanel(rows)];
}

export function buildGrandTotalSection(reportModel) {
  return buildGrandTotalSectionFromPresentation(
    getEstimateTotalPresentation(reportModel),
  );
}

export function buildNotesSection(notes, title = "Notes") {
  if (!notes) return null;

  return [
    pdfGoldRule(),
    { text: title, style: "sectionTitle" },
    { text: notes, style: "notes" },
  ];
}

/**
 * Closing invoice footer — no section title; muted text only.
 */
export function buildInvoiceFooterSection(invoiceFooter) {
  const text = invoiceFooter?.text || null;
  if (!text) return null;

  return [
    pdfGoldRule(),
    {
      text,
      style: "invoiceFooter",
      margin: [0, 10, 0, 2],
    },
  ];
}

/**
 * Render structured payment instructions presentation blocks for pdfmake.
 * Method-agnostic: only consumes title + blocks from the presentation model.
 */
export function buildPaymentInstructionsSection(paymentInstructions) {
  if (!paymentInstructions?.blocks?.length) return null;

  const content = [
    pdfGoldRule(),
    {
      text: paymentInstructions.title || "How to Pay",
      style: "sectionTitle",
      margin: [0, 2, 0, 8],
    },
  ];

  paymentInstructions.blocks.forEach((block) => {
    if (block.type === "field") {
      content.push({
        columns: [
          {
            width: 120,
            text: `${block.label}:`,
            style: "metaLabel",
          },
          {
            width: "*",
            text: block.value || "",
            style: "metaValue",
          },
        ],
        columnGap: 8,
        margin: [0, 0, 0, 4],
      });
      return;
    }

    if (block.type === "text") {
      content.push({
        text: block.text || "",
        style: "notes",
        margin: [0, 2, 0, 4],
      });
    }
  });

  return content;
}

export function buildStandardFooter(generatedDate, footerText = "Organization") {
  return (currentPage, pageCount) => ({
    columns: [
      { text: footerText, style: "footer" },
      {
        text: generatedDate || "",
        style: "footer",
        alignment: "center",
      },
      {
        text: `${currentPage} / ${pageCount}`,
        style: "footer",
        alignment: "right",
      },
    ],
    margin: [45, 0, 45, 24],
  });
}
